import { ReactElement } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import Placeholder from 'assets/common/placeholder.png';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { FileIcon, FolderIcon, ScienceIcon } from 'assets/icons/v2';
import { useFirebase } from 'hooks/useFirebase';
import mask from 'assets/common/folder-mask.png';
import {
  useIsInModal,
  useModalNavigation,
} from '../Modal/variants/worksheet/components/ModalNavigationContext';

export type FolderBoxProps = {
  id: string;
  name: string;
  numOfArticles: number;
  numOfSubfolders: number;
  numOfMedications: number;
  image?: string | null;
};

export const FolderBox = ({
  id,
  name,
  numOfArticles,
  numOfSubfolders,
  numOfMedications,
  image,
}: FolderBoxProps): ReactElement => {
  const navigate = useNavigate();
  const isInModal = useIsInModal();
  const modalNavigation = useModalNavigation();
  const { logFirebaseClick } = useFirebase();

  const folderName = name || '';
  const shouldShowArticleNum = numOfArticles > 0;
  const shouldShowFolderNum = numOfSubfolders > 0;
  const shouldShowMedicationNum = numOfMedications > 0;

  const handleClick = (): void => {
    logFirebaseClick('folder-card', folderName);
    if (isInModal && modalNavigation) {
      modalNavigation.navigate({
        name: 'folder',
        params: { id },
      });
      return;
    }
    navigate(`${ProtectedRoutes.FOLDER}/${id}`);
  };

  return (
    <HStack
      onClick={handleClick}
      borderRadius="9px"
      position="relative"
      bg="background.primary"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      overflow="hidden"
      minH="110px"
      gap="60px"
      _hover={{ bg: 'background.canvas' }}
      _active={{ bg: 'background.canvas' }}
      border="default"
      borderColor="border.accent"
    >
      <VStack
        align="left"
        w="full"
        p="15px 0px 15px 20px"
        justifyContent="space-between"
        gap="l"
        minHeight="100%"
      >
        <Text color="text.primary" textAlign="left" textStyle="h4.regular">
          {folderName}
        </Text>
        <HStack>
          {shouldShowArticleNum && (
            <HStack gap="5px">
              <FileIcon color="icon.tertiary" boxSize="18px" />
              <Text color="text.tertiary" textStyle="body.s.regular">
                {numOfArticles} Lernkarten
              </Text>
            </HStack>
          )}
          {shouldShowFolderNum && (
            <HStack gap="5px">
              <FolderIcon color="icon.tertiary" boxSize="18px" />
              <Text color="text.tertiary" textStyle="body.s.regular">
                {numOfSubfolders} Ordner
              </Text>
            </HStack>
          )}
          {shouldShowMedicationNum && (
            <HStack gap="5px">
              <ScienceIcon color="icon.tertiary" boxSize="18px" />
              <Text color="text.tertiary" textStyle="body.s.regular">
                {numOfMedications} Medikamente
              </Text>
            </HStack>
          )}
        </HStack>
      </VStack>
      <Box
        minW="165px"
        height="100%"
        backgroundImage={`url(${image || Placeholder})`}
        backgroundPosition="center"
        backgroundSize="cover"
        backgroundRepeat="no-repeat"
        style={{
          WebkitMaskImage: `url(${mask})`,
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: 'cover',
          maskImage: `url(${mask})`,
          maskRepeat: 'no-repeat',
          maskSize: 'cover',
          maskPosition: 'center',
        }}
      />
    </HStack>
  );
};
